import React from 'react';
import PropTypes from 'prop-types';
import colors from '@loggi/ui/components/utils/colors';
import { deprecated } from '@loggi/ui/components/utils/warnings';
import {
  BREAKPOINTS,
  GUTTERS,
  HORIZONTAL_GUTTER_SIZES,
  VERTICAL_GUTTER_SIZES,
  gutterShape
} from './types.js';

const { getColElement } = __NATIVE__ ? require('./native') : require('./dom');

class Col extends React.Component {
  constructor(props) {
    super(props);

    if (__DEV__) {
      const boundDeprecated = deprecated.bind(this, 'Col');

      if (this.props.gutter === 'left') {
        boundDeprecated('gutter="left"', '[breakpoint]GutterLeft prop');
      }

      if (this.props.gutter === 'right') {
        boundDeprecated('gutter="right"', '[breakpoint]GutterRight prop');
      }

      if (this.props.xs) {
        boundDeprecated('xs', 'size={xs:(value) ... }');
      }
      if (this.props.sm) {
        boundDeprecated('sm', 'size={ ... sm:(value)}');
      }
      if (this.props.md) {
        boundDeprecated('md', 'size={ ... md:(value)}');
      }
      if (this.props.lg) {
        boundDeprecated('lg', 'size={ ... lg:(value)}');
      }
      if (this.props.xl) {
        boundDeprecated('xl', 'size={ ... xl:(value)}');
      }
      if (this.props.xxl) {
        boundDeprecated('xxl', 'size={ ... xxl:(value)}');
      }
      if (this.props.xsOffset) {
        boundDeprecated('xsOffset', 'offset={xs:(value) ... }');
      }
      if (this.props.smOffset) {
        boundDeprecated('smOffset', 'offset={ ... sm:(value)}');
      }
      if (this.props.mdOffset) {
        boundDeprecated('mdOffset', 'offset={ ... md:(value)}');
      }
      if (this.props.lgOffset) {
        boundDeprecated('lgOffset', 'offset={ ... lg:(value)}');
      }
      if (this.props.xlOffset) {
        boundDeprecated('xlOffset', 'offset={ ... xl:(value)}');
      }
      if (this.props.xxlOffset) {
        boundDeprecated('xxlOffset', 'offset={ ... xxl:(value)}');
      }
    }
  }

  propMeasure(prop, oldProps) {
    let shape = oldProps || {};
    if (prop) {
      if (typeof prop === 'number') {
        shape = Object.assign(shape, { xs: prop });
      } else if (typeof prop === 'object') {
        shape = Object.assign(shape, prop);
      }
    }
    shape = Object.assign(shape, oldProps);
    return shape;
  }

  gutterPropMeasure(prop, oldProps) {
    if (prop) {
      if (prop === true) {
        let groupGutter = this.groupGutters(null, {
          left: 'normal',
          right: 'normal'
        });
        return this.groupGuttersByBreakpoint({ xs: groupGutter });
      } else if (typeof prop === 'string') {
        let groupGutter = this.groupGutters(prop);
        if (prop === 'left') {
          groupGutter = this.groupGutters(null, { left: 'normal' });
        } else if (prop === 'right') {
          groupGutter = this.groupGutters(null, { right: 'normal' });
        }
        return this.groupGuttersByBreakpoint({ xs: groupGutter });
      } else if (typeof prop === 'object') {
        const propKeys = Object.keys(prop);
        const gutterIntersection = propKeys.filter(
          n => GUTTERS.indexOf(n) !== -1
        );
        const breakpointIntersection = propKeys.filter(
          n => BREAKPOINTS.indexOf(n) !== -1
        );
        if (gutterIntersection.length) {
          const gutterFragment = this.groupGutters(false, prop);
          return this.groupGuttersByBreakpoint({ xs: gutterFragment });
        } else if (breakpointIntersection.length) {
          // TODO: validate the value inside of breakpoint object received
          return this.groupGuttersByBreakpoint(prop);
        }
      }
      return prop;
    }
    return oldProps ? oldProps : null;
  }

  groupPropsByBreakpoint(xs, sm, md, lg, xl, xxl) {
    return {
      xs: xs || null,
      sm: sm || null,
      md: md || null,
      lg: lg || null,
      xl: xl || null,
      xxl: xxl || null
    };
  }

  groupGuttersByBreakpoint(breakpoints) {
    return {
      xs: breakpoints.xs || gutterShape,
      sm: breakpoints.sm || gutterShape,
      md: breakpoints.md || gutterShape,
      lg: breakpoints.lg || gutterShape,
      xl: breakpoints.xl || gutterShape,
      xxl: breakpoints.xxl || gutterShape
    };
  }

  groupGutters(general, shape) {
    let newShape = Object.assign({}, gutterShape, shape);
    return {
      top: general || newShape.top,
      right: general || newShape.right,
      bottom: general || newShape.bottom,
      left: general || newShape.left
    };
  }

  render() {
    const {
      backgroundColor,
      size,
      gutter,
      offset,
      push,
      pull,
      xs,
      sm,
      md,
      lg,
      xl,
      xxl,
      xsOffset,
      smOffset,
      mdOffset,
      lgOffset,
      xlOffset,
      xxlOffset,
      xsPull,
      smPull,
      mdPull,
      lgPull,
      xlPull,
      xxlPull,
      xsPush,
      smPush,
      mdPush,
      lgPush,
      xlPush,
      xxlPush,
      xsGutter,
      smGutter,
      mdGutter,
      lgGutter,
      xlGutter,
      xxlGutter,
      xsGutterTop,
      xsGutterBottom,
      xsGutterLeft,
      xsGutterRight,
      smGutterTop,
      smGutterBottom,
      smGutterLeft,
      smGutterRight,
      mdGutterTop,
      mdGutterBottom,
      mdGutterLeft,
      mdGutterRight,
      lgGutterTop,
      lgGutterBottom,
      lgGutterLeft,
      lgGutterRight,
      xlGutterTop,
      xlGutterBottom,
      xlGutterLeft,
      xlGutterRight,
      xxlGutterTop,
      xxlGutterBottom,
      xxlGutterLeft,
      xxlGutterRight,
      className,
      flex,
      ...otherProps
    } = this.props;

    // oldSizes
    const oldSizes = this.groupPropsByBreakpoint(xs, sm, md, lg, xl, xxl);
    const sizes = this.propMeasure(size, oldSizes);

    // oldOffsets
    const oldOffsets = this.groupPropsByBreakpoint(
      xsOffset,
      smOffset,
      mdOffset,
      lgOffset,
      xlOffset,
      xxlOffset
    );
    const offsets = this.propMeasure(offset, oldOffsets);
    // oldPulls
    const oldPulls = this.groupPropsByBreakpoint(
      xsPull,
      smPull,
      mdPull,
      lgPull,
      xlPull,
      xxlPull
    );
    const pulls = this.propMeasure(pull, oldPulls);
    // oldPushs
    const oldPushs = this.groupPropsByBreakpoint(
      xsPush,
      smPush,
      mdPush,
      lgPush,
      xlPush,
      xxlPush
    );
    const pushs = this.propMeasure(push, oldPushs);

    // oldGutters
    const oldGutters = this.groupPropsByBreakpoint(
      this.groupGutters(xsGutter, {
        top: xsGutterTop,
        right: xsGutterRight,
        bottom: xsGutterBottom,
        left: xsGutterLeft
      }),
      this.groupGutters(smGutter, {
        top: smGutterTop,
        right: smGutterRight,
        bottom: smGutterBottom,
        left: smGutterLeft
      }),
      this.groupGutters(mdGutter, {
        top: mdGutterTop,
        right: mdGutterRight,
        bottom: mdGutterBottom,
        left: mdGutterLeft
      }),
      this.groupGutters(lgGutter, {
        top: lgGutterTop,
        right: lgGutterRight,
        bottom: lgGutterBottom,
        left: lgGutterLeft
      }),
      this.groupGutters(xlGutter, {
        top: xlGutterTop,
        right: xlGutterRight,
        bottom: xlGutterBottom,
        left: xlGutterLeft
      }),
      this.groupGutters(xxlGutter, {
        top: xxlGutterTop,
        right: xxlGutterRight,
        bottom: xxlGutterBottom,
        left: xxlGutterLeft
      })
    );
    const gutters = this.gutterPropMeasure(gutter, oldGutters);

    const classList = [
      { 'no-gutter': !this.props.gutter },
      { [`background-${backgroundColor}`]: backgroundColor },
      { [`col-xs-${sizes.xs}`]: sizes.xs },
      { [`col-sm-${sizes.sm}`]: sizes.sm },
      { [`col-md-${sizes.md}`]: sizes.md },
      { [`col-lg-${sizes.lg}`]: sizes.lg },
      { [`col-xl-${sizes.xl}`]: sizes.xl },
      { [`col-xxl-${sizes.xxl}`]: sizes.xxl },
      { [`col-xs-offset-${offsets.xs}`]: offsets.xs },
      { [`col-sm-offset-${offsets.sm}`]: offsets.sm },
      { [`col-md-offset-${offsets.md}`]: offsets.md },
      { [`col-lg-offset-${offsets.lg}`]: offsets.lg },
      { [`col-xl-offset-${offsets.xl}`]: offsets.xl },
      { [`col-xxl-offset-${offsets.xxl}`]: offsets.xxl },
      { [`col-xs-pull-${pulls.xs}`]: pulls.xs },
      { [`col-sm-pull-${pulls.sm}`]: pulls.sm },
      { [`col-md-pull-${pulls.md}`]: pulls.md },
      { [`col-lg-pull-${pulls.lg}`]: pulls.lg },
      { [`col-xl-pull-${pulls.xl}`]: pulls.xl },
      { [`col-xxl-pull-${pulls.xxl}`]: pulls.xxl },
      { [`col-xs-push-${pushs.xs}`]: pushs.xs },
      { [`col-sm-push-${pushs.sm}`]: pushs.sm },
      { [`col-md-push-${pushs.md}`]: pushs.md },
      { [`col-lg-push-${pushs.lg}`]: pushs.lg },
      { [`col-xl-push-${pushs.xl}`]: pushs.xl },
      { [`col-xxl-push-${pushs.xxl}`]: pushs.xxl },
      { [`gutter-xs-top-${gutters.xs.top}`]: !!gutters.xs.top },
      { [`gutter-sm-top-${gutters.sm.top}`]: !!gutters.sm.top },
      { [`gutter-md-top-${gutters.md.top}`]: !!gutters.md.top },
      { [`gutter-lg-top-${gutters.lg.top}`]: !!gutters.lg.top },
      { [`gutter-xl-top-${gutters.xl.top}`]: !!gutters.xl.top },
      { [`gutter-xxl-top-${gutters.xxl.top}`]: !!gutters.xxl.top },
      { [`gutter-xs-bottom-${gutters.xs.bottom}`]: gutters.xs.bottom },
      { [`gutter-sm-bottom-${gutters.sm.bottom}`]: gutters.sm.bottom },
      { [`gutter-md-bottom-${gutters.md.bottom}`]: gutters.md.bottom },
      { [`gutter-lg-bottom-${gutters.lg.bottom}`]: gutters.lg.bottom },
      { [`gutter-xl-bottom-${gutters.xl.bottom}`]: gutters.xl.bottom },
      { [`gutter-xxl-bottom-${gutters.xxl.bottom}`]: gutters.xxl.bottom },
      { [`gutter-xs-left-${gutters.xs.left}`]: gutters.xs.left },
      { [`gutter-sm-left-${gutters.sm.left}`]: gutters.sm.left },
      { [`gutter-md-left-${gutters.md.left}`]: gutters.md.left },
      { [`gutter-lg-left-${gutters.lg.left}`]: gutters.lg.left },
      { [`gutter-xl-left-${gutters.xl.left}`]: gutters.xl.left },
      { [`gutter-xxl-left-${gutters.xxl.left}`]: gutters.xxl.left },
      { [`gutter-xs-right-${gutters.xs.right}`]: gutters.xs.right },
      { [`gutter-sm-right-${gutters.sm.right}`]: gutters.sm.right },
      { [`gutter-md-right-${gutters.md.right}`]: gutters.md.right },
      { [`gutter-lg-right-${gutters.lg.right}`]: gutters.lg.right },
      { [`gutter-xl-right-${gutters.xl.right}`]: gutters.xl.right },
      { [`gutter-xxl-right-${gutters.xxl.right}`]: gutters.xxl.right },
      { ['col-flex']: flex }
    ];

    return getColElement(className, classList, otherProps, this.props.children);
  }
}

Col.displayName = 'Col';
Col.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.string
  ]),
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  offset: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  pull: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  push: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  gutter: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onSelect: PropTypes.func,
  flex: PropTypes.bool,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object
  ]),
  backgroundColor: PropTypes.oneOf(colors.backgroundHexKeys),
  xs: PropTypes.string,
  sm: PropTypes.string,
  md: PropTypes.string,
  lg: PropTypes.string,
  xl: PropTypes.string,
  xxl: PropTypes.string,
  xsOffset: PropTypes.string,
  smOffset: PropTypes.string,
  mdOffset: PropTypes.string,
  lgOffset: PropTypes.string,
  xlOffset: PropTypes.string,
  xxlOffset: PropTypes.string,
  xsPull: PropTypes.string,
  smPull: PropTypes.string,
  mdPull: PropTypes.string,
  lgPull: PropTypes.string,
  xlPull: PropTypes.string,
  xxlPull: PropTypes.string,
  xsPush: PropTypes.string,
  smPush: PropTypes.string,
  mdPush: PropTypes.string,
  lgPush: PropTypes.string,
  xlPush: PropTypes.string,
  xxlPush: PropTypes.string,
  xsGutter: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  smGutter: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  mdGutter: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  lgGutter: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  xlGutter: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  xxlGutter: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  xsGutterLeft: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  smGutterLeft: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  mdGutterLeft: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  lgGutterLeft: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  xlGutterLeft: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  xxlGutterLeft: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  xsGutterRight: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  smGutterRight: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  mdGutterRight: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  lgGutterRight: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  xlGutterRight: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  xxlGutterRight: PropTypes.oneOf(HORIZONTAL_GUTTER_SIZES),
  xsGutterTop: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  smGutterTop: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  mdGutterTop: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  lgGutterTop: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  xlGutterTop: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  xxlGutterTop: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  xsGutterBottom: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  smGutterBottom: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  mdGutterBottom: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  lgGutterBottom: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  xlGutterBottom: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  xxlGutterBottom: PropTypes.oneOf(VERTICAL_GUTTER_SIZES),
  debug: PropTypes.bool
};

Col.defaultProps = {
  size: null,
  gutter: null,
  offset: null,
  pull: null,
  push: null,
  onClick: null,
  backgroundColor: null,

  xs: null,
  sm: null,
  md: null,
  lg: null,
  xl: null,
  xxl: null,
  xsOffset: null,
  smOffset: null,
  mdOffset: null,
  lgOffset: null,
  xlOffset: null,
  xxlOffset: null,
  xsPull: null,
  smPull: null,
  mdPull: null,
  lgPull: null,
  xlPull: null,
  xxlPull: null,
  xsPush: null,
  smPush: null,
  mdPush: null,
  lgPush: null,
  xlPush: null,
  xxlPush: null,
  xsGutter: null,
  smGutter: null,
  mdGutter: null,
  lgGutter: null,
  xlGutter: null,
  xxlGutter: null,
  xsGutterLeft: null,
  smGutterLeft: null,
  mdGutterLeft: null,
  lgGutterLeft: null,
  xlGutterLeft: null,
  xxlGutterLeft: null,
  xsGutterRight: null,
  smGutterRight: null,
  mdGutterRight: null,
  lgGutterRight: null,
  xlGutterRight: null,
  xxlGutterRight: null,
  xsGutterTop: null,
  smGutterTop: null,
  mdGutterTop: null,
  lgGutterTop: null,
  xlGutterTop: null,
  xxlGutterTop: null,
  xsGutterBottom: null,
  smGutterBottom: null,
  mdGutterBottom: null,
  lgGutterBottom: null,
  xlGutterBottom: null,
  xxlGutterBottom: null,
  flex: false
};

export default Col;
export { COL_SIZES as SIZES } from './types.js';
